import {useRef, useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Swal from 'sweetalert2';
import {LocalizationProvider, TimePicker} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {DemoItem} from '@mui/x-date-pickers/internals/demo';
import {fetchPatientDetailsUuid, postPatientReport} from '../Redux/Thunk/Actions';
import '../styles/PatientReportFileTemplateCreate.scss';
import '../styles/MedicalReport.scss';
import {RenderSignature} from '../utils/templateUtils';
import AnalysisTable from './AnalysisTable';
import FigmaHeader from './FigmaHeader/FigmaHeader';
import MedicalReportTemplate, {formatDateToDDMMYYYY} from './MedicalReportTemplate';
import embryologyLogo from '../assets/images/embryology-logo.png';

const isValidBranchAwareUhid = value => /^(ASCAS|VDASCAS)\d+$/i.test((value || '').trim());

// Configuration Constants
const COLLECTION_SITE_OPTIONS = [
  {value: '', label: 'Select Collection Type'},
  {value: 'Center', label: 'Center'},
  {value: 'Home', label: 'Home'},
];

const GENDER_OPTIONS = [
  {value: '', label: 'Select Gender'},
  {value: 'Male', label: 'Male'},
  {value: 'Female', label: 'Female'},
  {value: 'Others', label: 'Others'},
  {value: 'Decline to State', label: 'Decline to State'}
];

const SAMPLE_DETAILS_ROWS = [
  {
    label: 'Sample Collection Type',
    name: 'sampleCollectionSite',
    type: 'custom',
    renderInput: (values, onChange, styles, show) => {
      const sampleDetails = values.sampleDetails || {};
      if (show) {
        return (
          <span>
            {sampleDetails.sampleCollectionSite}
            {sampleDetails.sampleCollectionSite === 'Other' && sampleDetails.otherSite ? `: ${sampleDetails.otherSite}` : ''}
          </span>
        );
      }
      return (
        <div style={{display: 'flex', gap: '20px', alignItems: 'center', justifyContent: 'flex-start'}}>
          <select name='sampleCollectionSite' value={sampleDetails.sampleCollectionSite || ''} onChange={onChange} style={{...styles.input, width: '180px'}}>
            {COLLECTION_SITE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {sampleDetails.sampleCollectionSite === 'Other' && (
            <input type='text' name='otherSite' placeholder='Specify other site' value={sampleDetails.otherSite || ''} onChange={onChange} style={{...styles.input, width: '180px'}} />
          )}
        </div>
      );
    }
  },
  {
    label: 'Date of Collection',
    name: 'dateOfCollection',
    type: 'date',
    width: '180px'
  },
  {
    label: 'Date of Reporting',
    name: 'dateOfReporting',
    type: 'date',
    width: '180px'
  },
  {
    label: 'Mode of Collection',
    name: 'modeOfCollection',
    type: 'text',
    width: '180px'
  },
  {
    label: 'Time of Collection',
    name: 'timeOfCollection',
    type: 'timepicker',
    renderInput: () => 'TIME_PICKER_COMPONENT' // Special marker for AnalysisTable
  },
  {
    label: 'Time of Examination',
    name: 'timeOfExamination',
    type: 'timepicker',
    renderInput: () => 'TIME_PICKER_COMPONENT' // Special marker for AnalysisTable
  },
  {
    label: 'Abstinence Period',
    name: 'abstinencePeriod',
    type: 'number',
    unit: 'Days',
    width: '180px',
    placeholder: 'Days'
  },
  {
    label: 'Complete Collection',
    name: 'completeCollection',
    type: 'select',
    width: '180px',
    options: [
      {value: '', label: 'Select'},
      {value: 'Yes', label: 'Yes'},
      {value: 'No', label: 'No'}
    ]
  }
];

const MACROSCOPIC_ROWS = [
  {
    label: 'Volume',
    name: 'volume',
    type: 'text',
    unit: '(ml)',
    width: '150px',
    reference: '>=1.5 ml'
  },
  {
    label: 'Appearance and Colour',
    name: 'appearance',
    type: 'text',
    width: '150px',
    reference: 'Whitish/ Greyish Opalescent'
  },
  {
    label: 'Viscosity',
    name: 'viscosity',
    type: 'text',
    width: '150px',
    reference: 'No Reference'
  },
  {
    label: 'pH',
    name: 'pH',
    type: 'text',
    width: '150px',
    reference: '7.2-7.8'
  },
  {
    label: 'Time of Liquefaction',
    name: 'liquefactionTime',
    type: 'number',
    unit: '(Mins)',
    width: '150px',
    reference: '<60 Mins'
  },
];

const MICROSCOPIC_ROWS = [
  {
    label: 'Sperm Concentration',
    name: 'spermConcentration',
    type: 'text',
    unit: '(millions/ml)',
    width: '150px',
    reference: '>=16 millions/ml '
  },
  {
    label: 'Total Sperm Number (per ejaculate)',
    name: 'totalSpermCount',
    type: 'text',
    unit: '(million)',
    width: '150px',
    reference: '>=39 million'
  },
  {
    label: 'Rapid Progressive Motility(a)',
    name: 'progressiveMotility',
    type: 'text',
    unit: '%',
    width: '150px',
    reference: '>=30% RPR+SPR(A+B)'
  },
  {
    label: 'Slow Progressive Motility(b)',
    name: 'slowProgressiveMotility',
    type: 'text',
    unit: '%',
    width: '150px',
    reference: '<=42% PR+NP(A+B+C)'
  },
  {
    label: 'Non-Progressive Motility(c)',
    name: 'nonProgressiveMotility',
    type: 'text',
    unit: '%',
    width: '150px',
    reference: ''
  },
  {
    label: 'Total Motility',
    name: 'totalMotility',
    type: 'text',
    unit: '%',
    width: '150px',
    reference: ''
  },
  {
    label: 'Immotile Sperm',
    name: 'immotileSperm',
    type: 'text',
    unit: '%',
    width: '150px',
    reference: ''
  },
  {
    label: 'Agglutination',
    name: 'agglutination',
    type: 'text',
    width: '150px',
    reference: ''
  },
  {
    label: 'Aggregation',
    name: 'aggregation',
    type: 'text',
    width: '150px',
    reference: ''
  },
  {
    label: 'Round Cells / HPF',
    name: 'roundCells',
    type: 'text',
    unit: 'cells/HPF',
    width: '150px',
    reference: ''
  },
  {
    label: 'Other Cells / Debris',
    name: 'otherCells',
    type: 'text',
    width: '150px',
    reference: ''
  },
  {
    label: 'Fructose',
    name: 'fructose',
    type: 'text',
    width: '150px',
    reference: ''
  },
  {
    label: 'Vitality',
    name: 'vitality',
    type: 'text',
    unit: '%',
    width: '150px',
    reference: '>54%'
  },
  {
    label: 'Total Motile Progressive(TMSC)',
    name: 'totalMotileProgressive',
    type: 'text',
    unit: 'volume*(a+b+c)*sperm concentration in million(formula)',
    width: '150px',
    reference: ''
  }
];

const MORPHOLOGY_ROWS = [
  {
    label: 'Normal Forms',
    name: 'normalForms',
    type: 'text',
    unit: '%',
    width: '150px',
    reference: '>4%'
  },
  {
    label: 'Abnormal Forms',
    name: 'abnormalForms',
    type: 'text',
    unit: '%',
    width: '150px'
  },
  {
    label: 'Type of Abnormalities',
    name: 'typeOfAbnormalities',
    type: 'custom',
    renderInput: (values, onChange, styles, show) => {
      const morphology = values.morphology || {};
      if (show) {
        return (
          <div style={{display: 'flex', flexDirection: 'row', gap: '4px', alignItems: 'center', justifyContent: 'flex-end'}}>
            <span>Head: {morphology.headAbnormalities}%,</span>
            <span>Midpiece: {morphology.midpieceAbnormalities}%,</span>
            <span>Tail: {morphology.tailAbnormalities}%,</span>
            <span>Cytoplasmic droplets: {morphology.cytoplasmicDroplets}%</span>
          </div>
        );
      }
      return (
        <div style={{display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'left'}}>
          <div style={{display: 'flex', alignItems: 'left', gap: '8px'}}>
            <span style={{width: '180px', textAlign: 'left'}}>Head:</span>
            <input type='text' name='headAbnormalities' value={morphology.headAbnormalities || ''} onChange={onChange} style={{...styles.input, width: '150px'}} />
            <span>%</span>
          </div>
          <div style={{display: 'flex', alignItems: 'left', gap: '8px'}}>
            <span style={{width: '180px', textAlign: 'left'}}>Midpiece:</span>
            <input type='text' name='midpieceAbnormalities' value={morphology.midpieceAbnormalities || ''} onChange={onChange} style={{...styles.input, width: '150px'}} />
            <span>%</span>
          </div>
          <div style={{display: 'flex', alignItems: 'left', gap: '8px'}}>
            <span style={{width: '180px', textAlign: 'left'}}>Tail:</span>
            <input type='text' name='tailAbnormalities' value={morphology.tailAbnormalities || ''} onChange={onChange} style={{...styles.input, width: '150px'}} />
            <span>%</span>
          </div>
          <div style={{display: 'flex', alignItems: 'left', gap: '8px'}}>
            <span style={{width: '180px', textAlign: 'left'}}>Cytoplasmic droplets:</span>
            <input type='text' name='cytoplasmicDroplets' value={morphology.cytoplasmicDroplets || ''} onChange={onChange} style={{...styles.input, width: '150px'}} />
            <span>%</span>
          </div>
        </div>
      );
    }
  }
];

const REFERENCE_VALUES = [
  [
    {value: 'Volume', bold: true},
    {value: ': >=1.5 ml', bold: false},
    {value: 'Appearance and Colour', bold: true},
    {value: ': Whitish/ Greyish Opalescent', bold: false},
    {value: 'Viscosity', bold: true},
    {value: ': No Reference', bold: false}
  ],
  [
    {value: 'pH', bold: true},
    {value: ': 7.2-7.8', bold: false},
    {value: 'Time of Liquefaction', bold: true},
    {value: ': <60 Mins', bold: false},
    {value: 'Sperm Concentration', bold: true},
    {value: ': >=16 millions/ml ', bold: false}
  ],
  [
    {value: 'Total Sperm Number (Per Ejaculate)', bold: true},
    {value: ': >=39 million', bold: false},
    {value: 'Vitality', bold: true},
    {value: ': >54 %', bold: false},
    {value: 'Normal Form', bold: true},
    {value: ': >4%', bold: false}
  ],
  [
    {value: 'Motility', bold: true},
    {value: ': >=30% RPR+SPR (a+b)\n: >=42% PR+NP(a+b+c)', bold: false},
   
  ]
];

// Note: renderMuiTime function moved inside component to access handleMuiTimeChange

// Common styles
const commonStyles = {
  input: {
    border: '1px solid #b0a4a4ff',
    outline: 'none',
    padding: '10px',
    backgroundColor: 'white',
    width: '100%',
    fontSize: '14px',
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 1
  },
  label: {
    fontWeight: 600,
    fontSize: '14px'
  },
  separator: {
    fontWeight: 600,
    fontSize: '14px'
  },
  section: {
    maxWidth: '97%',
    margin: '0 auto'
  }
};

// Section header component
const SectionHeader = ({title, className = 'spermiogram-head', inline = false}) => (
  <h2
    style={{
      textAlign: 'center',
      backgroundColor: title ? '#f7f7f7' : 'transparent',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      fontSize: '16px',
      fontWeight: '600',
      padding: '5px 0 5px 10px',
      margin: inline ? '0' : '8px 0'
    }}
    className={className}>
    {title}
  </h2>
);

// Reusable FormField component
const FormField = ({label, type = 'text', name, value, onChange, show, options = [], width = '200px', labelWidth = '160px', reference}) => {
  const inputStyle = {...commonStyles.input, width};

  if (show) {
    // Display mode (read-only)
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${labelWidth} 16px 1fr`,
          alignItems: 'center',
          gap: '8px'
        }}>
        <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
        <span style={commonStyles.separator}>:</span>
        <span style={{fontSize: '14px', textAlign: 'left'}}>{value}</span>
      </div>
    );
  }

  // Edit mode
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${labelWidth} 16px ${width}`,
        alignItems: 'center',
        gap: '8px'
      }}>
      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
      <span style={commonStyles.separator}>:</span>

      {type === 'select' ? (
        <select name={name} value={value} onChange={onChange} style={inputStyle}>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} style={inputStyle} />
      )}
    </div>
  );
};

// Patient Info Section component
const PatientInfoSection = ({patientNumber, partnerType, show, handleChange, valueChange}) => {
  return (
    <div className='' style={{width: '100%', display: 'block'}} id={`patientInfo${patientNumber}`}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '3px',
          backgroundColor: '#f7f7f7 ',
          borderRadius: '6px',
          padding: '8px 4px 8px 4px',
          gap: '3px'
        }}>
        {/* Patient ID */}
        <FormField label='Patient ID' name={`patientId${patientNumber}`} value={valueChange.patient[`patientId${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />

        {/* Patient Name */}
        <FormField
          label={`${partnerType} Partner Name`}
          name={`patientName${patientNumber}`}
          value={valueChange.patient[`patientName${patientNumber}`]}
          onChange={handleChange}
          show={show}
          width='250px'
          labelWidth='125px'
        />

        {/* Gender */}
        <FormField
          label='Gender'
          type='select'
          name={`gender${patientNumber}`}
          value={valueChange.patient[`gender${patientNumber}`]}
          onChange={handleChange}
          show={show}
          options={GENDER_OPTIONS}
          width='250px'
          labelWidth='125px'
        />

        {/* Age */}
        <FormField label='Age' name={`age${patientNumber}`} value={valueChange.patient[`age${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />
      </div>
    </div>
  );
};

// Sample Details Section Component
const SampleDetailsSection = ({show, handleChange, valueChange, commonStyles}) => (
  <div style={{marginBottom: '10px'}}>
    <AnalysisTable
      headers={['Parameter', 'Observation']}
      rows={SAMPLE_DETAILS_ROWS}
      show={show}
      onChange={handleChange}
      values={valueChange}
      commonStyles={commonStyles}
    />
  </div>
);

// Macroscopic Section Component
const MacroscopicSection = ({show, handleChange, valueChange, commonStyles}) => (
  <>
    <SectionHeader title='⚗️ Macroscopic Examination' />
    <div style={{marginBottom: '20px'}}>
      <AnalysisTable
        headers={['Parameter', 'Result', 'WHO Reference Values 2021 6th Edition ']}
        rows={MACROSCOPIC_ROWS}
        show={show}
        onChange={handleChange}
        values={valueChange}
        commonStyles={commonStyles}
      />
    </div>
  </>
);

// Microscopic Section Component
const MicroscopicSection = ({show, handleChange, valueChange, commonStyles}) => (
  <>
    <SectionHeader title='🔬 Microscopic Examination' />
    <div style={{marginBottom: '20px'}}>
      <AnalysisTable
        headers={['Parameter', 'Result', 'WHO Reference Values 2021 6th Edition ']}
        rows={MICROSCOPIC_ROWS}
        show={show}
        onChange={handleChange}
        values={valueChange}
        commonStyles={commonStyles}
      />
    </div>
  </>
);

// Morphology Section Component
const MorphologySection = ({show, handleChange, valueChange, commonStyles}) => (
  <>
    <SectionHeader title='🧫 Morphology' />
    <div style={{marginBottom: '20px'}}>
      <AnalysisTable
        headers={['Parameter', 'Result']}
        rows={MORPHOLOGY_ROWS}
        show={show}
        onChange={handleChange}
        values={valueChange}
        commonStyles={commonStyles}
      />
    </div>
  </>
);

// Impression Section Component
const ImpressionSection = ({show, handleChange, valueChange}) => (
  <div style={{marginBottom: '10px'}}>
    <SectionHeader title='🩺 Impression' inline={true} />
    {!show ? (
      <textarea
        name='impression'
        value={valueChange.impression}
        onChange={handleChange}
        style={{
          ...commonStyles.input,
          width: '100%',
          minHeight: '60px',
          padding: '10px',
          fontFamily: 'Arial, sans-serif',
          marginTop: '5px'
        }}
        placeholder='Enter clinical impression and diagnosis...'
      />
    ) : (
      valueChange.impression && (
        <div
          style={{
            border: '1px solid #ddd',
            padding: '10px',
            minHeight: '40px',
            backgroundColor: '#f9f9f9',
            whiteSpace: 'pre-wrap',
            fontFamily: 'Arial, sans-serif',
            marginTop: '5px'
          }}>
          {valueChange.impression}
        </div>
      )
    )}
  </div>
);

// Advice Section Component
const AdviceSection = ({show, handleChange, valueChange}) => (
  <div style={{marginBottom: '10px'}}>
    <SectionHeader title='📋 Comments' inline={true} />
    {!show ? (
      <textarea
        name='comments'
        value={valueChange.comments}
        onChange={handleChange}
        style={{
          ...commonStyles.input,
          width: '100%',
          minHeight: '60px',
          padding: '10px',
          fontFamily: 'Arial, sans-serif',
          marginTop: '5px'
        }}
        placeholder='Enter clinical comments and recommendations...'
      />
    ) : (
      valueChange.comments && (
        <div
          style={{
            border: '1px solid #ddd',
            padding: '10px',
            minHeight: '40px',
            backgroundColor: '#f9f9f9',
            whiteSpace: 'pre-wrap',
            fontFamily: 'Arial, sans-serif',
            marginTop: '5px'
          }}>
          {valueChange.comments}
        </div>
      )
    )}
  </div>
);

function SemenAnalysis(props) {
  const [show, setshow] = useState(false);
  const Content = useRef();
  const [valueChange, setvalueChange] = useState({
    // Patient Information
    patient: {
      gender1: 'Male',
      gender2: 'Female',
      patientId1: '',
      patientName1: '',
      age1: '',
      patientId2: '',
      patientName2: '',
      age2: ''
    },

    // Sample Details
    sampleDetails: {
      sampleCollectionSite: '',
      dayOfSample: '',
      dateOfCollection: new Date().toISOString().slice(0, 10),
      dateOfReporting: new Date().toISOString().slice(0, 10),
      modeOfCollection: 'Ejaculate',
      timeOfCollection: '',
      timeOfCollectionHour: '',
      timeOfCollectionMinute: '',
      timeOfCollectionPeriod: 'AM',
      timeOfExaminationHour: '',
      timeOfExaminationMinute: '',
      timeOfExaminationPeriod: 'AM',
      abstinencePeriod: '',
      completeCollection: '',
      otherSite: ''
    },

    // Macroscopic Examination
    macroscopic: {
      liquefactionTime: '',
      appearance: '',
      volume: '',
      pH: '',
      viscosity: ''
    },

    // Microscopic Examination
    microscopic: {
      spermConcentration: '',
      totalSpermCount: '',
      totalMotility: '',
      progressiveMotility: '',
      slowProgressiveMotility: '',
      nonProgressiveMotility: '',
      immotileSperm: '',
      fructose: '',
      totalMotileProgressive: '',
      vitality: '',
      agglutination: '',
      aggregation: '',
      roundCells: '',
      otherCells: ''
    },

    // Morphology
    morphology: {
      normalForms: '',
      abnormalForms: '',
      headAbnormalities: '',
      midpieceAbnormalities: '',
      tailAbnormalities: '',
      cytoplasmicDroplets: ''
    },

    // Impression & Comments
    impression: '',
    comments: '',

    // Reporting
    reporting: {
      technician: '',
      technicianOtherName: '',
      androtechName: '',
      consultantName: '',
      clinicianSignature: '',
      androtechSignature: ''
    }
  });

  const reportDate = new Date().toLocaleDateString('en-IN');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [patientOne_UUId, setPatientOne_UUId] = useState('');
  const [patientTwo_UUId, setPatientTwo_UUId] = useState('');

  // Calculate semen totals automatically
  useEffect(() => {
    const volume = parseFloat(valueChange.macroscopic.volume) || 0;
    const progressiveMotility = parseFloat(valueChange.microscopic.progressiveMotility) || 0;
    const slowProgressiveMotility = parseFloat(valueChange.microscopic.slowProgressiveMotility) || 0;
    const nonProgressiveMotility = parseFloat(valueChange.microscopic.nonProgressiveMotility) || 0;
    const spermConcentration = parseFloat(valueChange.microscopic.spermConcentration) || 0;

    const totalMotility = progressiveMotility + slowProgressiveMotility + nonProgressiveMotility;
    const totalSpermCount = volume * spermConcentration;
    const tmsc = volume * spermConcentration * (totalMotility / 100);
    const nextValues = {
      totalSpermCount: totalSpermCount > 0 ? totalSpermCount.toFixed(2) : '',
      totalMotility: totalMotility > 0 ? totalMotility.toFixed(2) : '',
      totalMotileProgressive: tmsc > 0 ? tmsc.toFixed(2) : ''
    };

    if (
      nextValues.totalSpermCount !== valueChange.microscopic.totalSpermCount ||
      nextValues.totalMotility !== valueChange.microscopic.totalMotility ||
      nextValues.totalMotileProgressive !== valueChange.microscopic.totalMotileProgressive
    ) {
      setvalueChange(prev => ({
        ...prev,
        microscopic: {
          ...prev.microscopic,
          ...nextValues
        }
      }));
    }
  }, [valueChange.macroscopic.volume, valueChange.microscopic.progressiveMotility, valueChange.microscopic.slowProgressiveMotility, valueChange.microscopic.nonProgressiveMotility, valueChange.microscopic.spermConcentration, valueChange.microscopic.totalSpermCount, valueChange.microscopic.totalMotility, valueChange.microscopic.totalMotileProgressive]);

  // MUI TimePicker handler - same as egg collection
  const handleMuiTimeChange = (e, name) => {
    let hours = e.$H,
      mins = e.$m,
      ampm = 'AM';
    let temMin = e.$m;
    temMin < 10 ? (mins = '0' + temMin) : (mins = temMin);
    if (e.$H >= 12) {
      if (e.$H === 12) {
        hours = 12;
        ampm = 'PM';
      } else {
        let temHour = e.$H - 12;
        temHour < 10 ? (hours = '0' + temHour) : (hours = temHour);
        ampm = 'PM';
      }
    } else if (e.$H === 0) {
      hours = 12;
      ampm = 'AM';
    } else {
      let temHour = e.$H;
      temHour < 10 ? (hours = '0' + temHour) : (hours = temHour);
      ampm = 'AM';
    }

    // Store in sampleDetails group
    setvalueChange({...valueChange, sampleDetails: {...valueChange.sampleDetails, [name]: `${hours}-${mins}-${ampm}`}});
  };

  // MUI TimePicker render function - same as egg collection
  const renderMuiTime = name => {
    return (
      <div className={!show ? 'show' : 'hide'}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoItem>
            <TimePicker onChange={e => handleMuiTimeChange(e, name)} />
          </DemoItem>
        </LocalizationProvider>
      </div>
    );
  };

  // Handle different input types
  const handleChange = e => {
    const text = e.target.value;

    // Handle patient ID fetching
    if (e.target.name === 'patientId1' || e.target.name === 'patientId2') {
      if (isValidBranchAwareUhid(text)) {
        const fetchData = async () => {
          const payload = {p_uhid: text};
          props.loaderchange('true');
          const addPatient = fetchPatientDetailsUuid(payload);
          const response = await dispatch(addPatient);
          if (response.payload?.status?.code === 200) {
            const isSinglePatient = response.payload.data.data.length === 1;
            if (isSinglePatient) {
              const data = response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;

              if (e.target.name === 'patientId1') {
                setPatientOne_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patient: {
                    ...valueChange.patient,
                    patientName1: namePat1,
                    age1: data?.age,
                    gender1: data?.gender,
                    patientId1: text
                  }
                });
              } else {
                setPatientTwo_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patient: {
                    ...valueChange.patient,
                    patientName2: namePat1,
                    age2: data?.age,
                    gender2: data?.gender,
                    patientId2: text
                  }
                });
              }
            } else {
              const data = response.payload.data.data[0].gender === 'Male' ? response.payload.data.data[0] : response.payload.data.data[1];
              const data2 = response.payload.data.data[1].gender === 'Female' ? response.payload.data.data[1] : response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;
              const namePat2 = data2?.titles + ' ' + data2?.first_name + ' ' + data2?.last_name;

              setPatientOne_UUId(data?.id);
              setPatientTwo_UUId(data2?.id);
              setvalueChange({
                ...valueChange,
                patient: {
                  ...valueChange.patient,
                  patientName1: namePat1,
                  patientName2: namePat2,
                  age1: data?.age,
                  age2: data2?.age,
                  gender1: data?.gender,
                  gender2: data2?.gender,
                  patientId1: data?.p_uhid,
                  patientId2: data2?.p_uhid
                }
              });
            }
            props.loaderchange('false');
          } else {
            props.loaderchange('false');
            props.popupalert('true');
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => {
              props.popupalert('false');
            }, 2000);
          }
        };
        fetchData();
    } else {
      setvalueChange({
        ...valueChange,
        patient: {
          ...valueChange.patient,
          [e.target.name]: text
        }
      });
    }
    }
    // Handle regular inputs
    else {
      // Determine which group this field belongs to
      let groupName = '';
      if (e.target.name.startsWith('patientId') || e.target.name.startsWith('patientName') || e.target.name.startsWith('age') || e.target.name.startsWith('gender')) {
        groupName = 'patient';
      } else if (e.target.name.includes('Collection') || e.target.name.includes('Examination') || e.target.name === 'dateOfReporting' || e.target.name === 'modeOfCollection' || e.target.name === 'abstinencePeriod' || e.target.name === 'completeCollection' || e.target.name === 'otherSite') {
        groupName = 'sampleDetails';
      } else if (e.target.name === 'volume' || e.target.name === 'appearance' || e.target.name === 'viscosity' || e.target.name === 'pH' || e.target.name === 'liquefactionTime') {
        groupName = 'macroscopic';
      } else if (e.target.name === 'spermConcentration' || e.target.name === 'totalSpermCount' || e.target.name === 'progressiveMotility' || e.target.name === 'slowProgressiveMotility' || e.target.name === 'nonProgressiveMotility' || e.target.name === 'totalMotility' || e.target.name === 'immotileSperm' || e.target.name === 'fructose' || e.target.name === 'totalMotileProgressive' || e.target.name === 'vitality' || e.target.name === 'agglutination' || e.target.name === 'aggregation' || e.target.name === 'roundCells' || e.target.name === 'otherCells') {
        groupName = 'microscopic';
      } else if (e.target.name === 'normalForms' || e.target.name === 'abnormalForms' || e.target.name === 'headAbnormalities' || e.target.name === 'midpieceAbnormalities' || e.target.name === 'tailAbnormalities' || e.target.name === 'cytoplasmicDroplets') {
        groupName = 'morphology';
      } else if (e.target.name === 'impression' || e.target.name === 'comments') {
        setvalueChange({...valueChange, [e.target.name]: text});
        return;
      } else if (
        e.target.name === 'technician' ||
        e.target.name === 'technicianOtherName' ||
        e.target.name === 'androtechName' ||
        e.target.name === 'consultantName' ||
        e.target.name === 'clinicianSignature' ||
        e.target.name === 'androtechSignature'
      ) {
        groupName = 'reporting';
      }

      if (groupName) {
        setvalueChange({
          ...valueChange,
          [groupName]: {
            ...valueChange[groupName],
            [e.target.name]: text
          }
        });
      }
    }
  };

  const handletest = async () => {
    try {
      if (
        patientOne_UUId !== '' &&
        valueChange.patient.patientId1 !== '' &&
        isValidBranchAwareUhid(valueChange.patient.patientId1) &&
        (valueChange.patient.patientId2 !== '' ? valueChange.patient.patientId1 !== valueChange.patient.patientId2 : true)
      ) {
        setshow(true);
        setTimeout(async () => {
          const htmlPayload = document.getElementById('reportcreate').innerHTML;
          const payload = {
            report_template: htmlPayload,
            patient_id: patientOne_UUId,
            description: '',
            patient_two: patientTwo_UUId,
            couple_id: valueChange.coupleId,
            template_type: 'Sperm Report'
          };

          props.loaderchange(true);
          const reportResponse = await dispatch(postPatientReport({type: '2', payload: payload}));

          if (reportResponse?.payload?.status?.code === 200) {
            props.loaderchange(false);
            navigate('/patientreports');
            Swal.fire({
              title: 'Successful.',
              text: 'Report added successful.',
              icon: 'success',
              showConfirmButton: false,
              timer: 1500
            });
          } else {
            setshow(false);
            props.loaderchange(false);
            props.popupalert(true);
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => {
              props.popupalert(false);
            }, 2000);
          }
        }, 3000);
      } else {
        Swal.fire({
          title: 'Error.',
          text: 'Please enter valid patient id.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500
        });
      }
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };

  const sideactive = useSelector(state => state.counter.sidebarnav);

  // Use constants instead of inline definitions

  return (
    <>
      <div className={sideactive ? 'maincontainer-patient-sa' : 'maincontainer-patient'}>
        <div className='row'>
          <div className='col'>
            <h3 className='page-title'>Report Template</h3>
            <ul className='breadcrumb'>
              <li className='breadcrumb-item'>
                <a href='/' style={{textDecoration: 'none', color: 'black'}}>
                  Dashboard /
                </a>
                <a href='/patientreports' style={{textDecoration: 'none', color: 'black'}}>
                  &nbsp;Patient Reports
                </a>
              </li>
              <li className='breadcrumb-item active'>Report Template</li>
            </ul>
          </div>
        </div>
        {/* <h1 style={{fontSize: '18px'}}>SEMEN ANALYSIS</h1> */}
        <div style={{backgroundColor: 'white', maxWidth: '100%'}} className='patientreport' id='reportcreate'>
          {show ? (
            <MedicalReportTemplate
              title='SEMEN ANALYSIS'
              clinicLogo={embryologyLogo}
              showDateTopRight={true}
              patientData={{
                patientOneId: valueChange.patient.patientId1,
                patientTwoId: valueChange.patient.patientId2,
                patientOneName: valueChange.patient.patientName1,
                patientTwoName: valueChange.patient.patientName2,
                genderOne: valueChange.patient.gender1,
                genderTwo: valueChange.patient.gender2,
                patientOneAge: valueChange.patient.age1,
                patientTwoAge: valueChange.patient.age2,
                date: reportDate,
                doctor: valueChange.reporting.consultantName, // This should come from data
                modeOfCollection: valueChange.sampleDetails.modeOfCollection,
                timeOfExamination: valueChange.sampleDetails.timeOfExamination || '',
                completeCollection: valueChange.sampleDetails.completeCollection,
                sampleCollection: valueChange.sampleDetails.sampleCollectionSite,
                timeOfCollection: valueChange.sampleDetails.timeOfCollection || '',
                sampleCollectionDate: valueChange.sampleDetails.dateOfCollection || '',
                abstinencePeriod: valueChange.sampleDetails.abstinencePeriod ? `${valueChange.sampleDetails.abstinencePeriod} Days` : '',
                technician: valueChange.reporting.technician,
                technicianOtherName: valueChange.reporting.technicianOtherName,
                androtechName: valueChange.reporting.androtechName,
                consultantName: valueChange.reporting.consultantName,
                clinicianSignature: valueChange.reporting.clinicianSignature,
                androtechSignature: ''
              }}
              sections={[
                {
                  title: '',
                  layout: 'custom-grid',
                  data: [
                    // Left column - first 4 items
                    [
                      {
                        label: 'Sample Collection Type',
                        value: valueChange.sampleDetails?.sampleCollectionSite === 'Other' ? `${valueChange.sampleDetails?.sampleCollectionSite}: ${valueChange.sampleDetails?.otherSite}` : valueChange.sampleDetails?.sampleCollectionSite || ''
                      },
                      {label: 'Date of Collection', value: formatDateToDDMMYYYY(valueChange.sampleDetails?.dateOfCollection) || ''},
                      {label: 'Date of Reporting', value: formatDateToDDMMYYYY(valueChange.sampleDetails?.dateOfReporting) || formatDateToDDMMYYYY(reportDate)},
                      {label: 'Mode of Collection', value: valueChange.sampleDetails?.modeOfCollection || ''}
                    ],
                    // Right column - remaining 4 items
                    [
                      {label: 'Time of Collection', value: valueChange.sampleDetails?.timeOfCollection || ''},
                      {label: 'Time of Examination', value: valueChange.sampleDetails?.timeOfExamination || ''},
                      {label: 'Abstinence Period', value: valueChange.sampleDetails?.abstinencePeriod ? `${valueChange.sampleDetails.abstinencePeriod} Days` : ''},
                      {label: 'Complete Collection', value: valueChange.sampleDetails?.completeCollection || ''}
                    ]
                  ]
                },
                {
                  title: 'Macroscopic Examination',
                  layout: 'custom-grid',
                  data: [
                    // Left column - first 3 items
                    [
                      {label: 'Volume', value: valueChange.macroscopic?.volume ? `${valueChange.macroscopic.volume} ${MACROSCOPIC_ROWS[0].unit}` : ''},
                      {label: 'Appearance and Colour', value: valueChange.macroscopic?.appearance || ''},
                      {label: 'Viscosity', value: valueChange.macroscopic?.viscosity || ''}
                    ],
                    // Right column - remaining 2 items
                    [
                      {label: 'pH', value: valueChange.macroscopic?.pH || ''},
                      {label: 'Time of Liquefaction', value: valueChange.macroscopic?.liquefactionTime ? `${valueChange.macroscopic.liquefactionTime} ${MACROSCOPIC_ROWS[4].unit}` : ''}
                    ]
                  ]
                },
                {
                  title: 'Microscopic Examination',
                  layout: 'custom-grid',
                  data: [
                    // Left column - first 6 items
                    [
                      {label: 'Sperm Concentration', value: valueChange.microscopic?.spermConcentration ? `${valueChange.microscopic.spermConcentration} ${MICROSCOPIC_ROWS[0].unit}` : ''},
                      {label: 'Total Sperm Number (per ejaculate)', value: valueChange.microscopic?.totalSpermCount ? `${valueChange.microscopic.totalSpermCount} ${MICROSCOPIC_ROWS[1].unit}` : ''},
                      {label: 'Rapid Progressive Motility(a)', value: valueChange.microscopic?.progressiveMotility ? `${valueChange.microscopic.progressiveMotility} ${MICROSCOPIC_ROWS[2].unit}` : ''},
                      {label: 'Slow Progressive Motility(b)', value: valueChange.microscopic?.slowProgressiveMotility ? `${valueChange.microscopic.slowProgressiveMotility} ${MICROSCOPIC_ROWS[3].unit}` : ''},
                      {label: 'Non-Progressive Motility(c)', value: valueChange.microscopic?.nonProgressiveMotility ? `${valueChange.microscopic.nonProgressiveMotility} ${MICROSCOPIC_ROWS[4].unit}` : ''},
                      {label: 'Total Motility', value: valueChange.microscopic?.totalMotility ? `${valueChange.microscopic.totalMotility} %` : ''},
                      {label: 'Immotile Sperm', value: valueChange.microscopic?.immotileSperm ? `${valueChange.microscopic.immotileSperm} %` : ''},
                      {label: 'Agglutination', value: valueChange.microscopic?.agglutination || ''},
                      {label: 'Aggregation', value: valueChange.microscopic?.aggregation || ''}
                    ],
                    // Right column - remaining 5 items
                    [
                      {label: 'Fructose', value: valueChange.microscopic?.fructose || ''},
                      {label: 'Vitality', value: valueChange.microscopic?.vitality ? `${valueChange.microscopic.vitality} %` : ''},
                      {label: 'Total Motile Progressive(TMSC)', value: valueChange.microscopic?.totalMotileProgressive ? `${valueChange.microscopic.totalMotileProgressive} million` : ''},
                      {label: 'Round Cells / HPF', value: valueChange.microscopic?.roundCells ? `${valueChange.microscopic.roundCells} cells/HPF` : ''},
                      {label: 'Other Cells / Debris', value: valueChange.microscopic?.otherCells || ''}
                    ]
                  ]
                },
                {
                  title: 'Morphology',
                  layout: 'custom-grid',
                  data: [
                    // Left column - first 3 items
                    [
                      {label: 'Normal Forms', value: valueChange.morphology?.normalForms ? `${valueChange.morphology.normalForms}%` : ''},
                      {label: 'Abnormal Forms', value: valueChange.morphology?.abnormalForms ? `${valueChange.morphology.abnormalForms}%` : ''},
                      {label: 'Head Abnormalities', value: valueChange.morphology?.headAbnormalities ? `${valueChange.morphology.headAbnormalities}%` : ''}
                    ],
                    // Right column - remaining 3 items
                    [
                      {label: 'Midpiece Abnormalities', value: valueChange.morphology?.midpieceAbnormalities ? `${valueChange.morphology.midpieceAbnormalities}%` : ''},
                      {label: 'Tail Abnormalities', value: valueChange.morphology?.tailAbnormalities ? `${valueChange.morphology.tailAbnormalities}%` : ''},
                      {label: 'Cytoplasmic Droplets', value: valueChange.morphology?.cytoplasmicDroplets ? `${valueChange.morphology.cytoplasmicDroplets}%` : ''}
                    ]
                  ]
                }
              ]}
              impression={valueChange.impression}
              comments={valueChange.comments}
              referenceValues={REFERENCE_VALUES}
              footerText={`Lab Reference: SA-2026-${new Date().toISOString().slice(0, 10).replace(/-/g, '')} | WHO Reference Values 2021 6th Edition`}
            />
          ) : (
            <>
              {/* Patient Information Header */}
              {/* Component-level print styles for single-page fit */}

              <FigmaHeader />

              {/* Patient & Couple Details */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: '3px',
                  border: '1px solid #000',
                  marginBottom: '5px'
                }}
                className='report-template-wrap1'>
                <PatientInfoSection patientNumber={1} partnerType='Male' show={show} handleChange={handleChange} valueChange={valueChange} />
                <div className='patientreportcreate2' style={{width: '100%', height: '100%', backgroundColor: '#f7f7f7'}} id='patientInfo2'>
                  <PatientInfoSection patientNumber={2} partnerType='Female' show={show} handleChange={handleChange} valueChange={valueChange} />
                </div>
              </div>
              <div style={{textAlign: 'center', backgroundColor: '#f7f7f7', padding: '5px 10px'}}>
                <h2 style={{
                  fontSize: '16px',
                  lineHeight: '1.2',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  color: 'black',
                  margin: '0',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                }}>SEMEN ANALYSIS</h2>
              </div>

              <div style={{padding: '0px 10px'}}>
                {/* 🔬 SAMPLE DETAILS */}
                <SampleDetailsSection show={show} handleChange={handleChange} valueChange={valueChange} commonStyles={commonStyles} />

                {/* ⚗️ MACROSCOPIC EXAMINATION */}
                <MacroscopicSection show={show} handleChange={handleChange} valueChange={valueChange} commonStyles={commonStyles} />

                {/* 🔬 MICROSCOPIC EXAMINATION */}
                <MicroscopicSection show={show} handleChange={handleChange} valueChange={valueChange} commonStyles={commonStyles} />

                {/* 🧫 MORPHOLOGY (STRICT CRITERIA – WHO 2021) */}
                <MorphologySection show={show} handleChange={handleChange} valueChange={valueChange} commonStyles={commonStyles} />

                {/* 🩺 IMPRESSION */}
                {/* <ImpressionSection show={show} handleChange={handleChange} valueChange={valueChange} /> */}

                {/* 💡 ADVICE */}
                <AdviceSection show={show} handleChange={handleChange} valueChange={valueChange} />

                {/* Technician and Clinician Signatures */}
                <RenderSignature show={show} valueChange={valueChange} handleChange={handleChange} commonStyles={commonStyles} />
              </div>
            </>
          )}
        </div>

        <div className='Footer'>
          <button onClick={handletest} className='Button_Create'>
            Add Report
          </button>
        </div>
      </div>
    </>
  );
}

export default SemenAnalysis;
